// Period presets — dates are strings in "YYYY-MM-DD" format.

const pad = (n) => String(n).padStart(2, "0");
export const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

// Monday as start of week
function startOfWeek(d) {
  const x = startOfDay(d);
  const dow = x.getDay(); // 0=Sun..6=Sat
  const shift = dow === 0 ? 6 : dow - 1;
  x.setDate(x.getDate() - shift);
  return x;
}

function endOfWeek(d) {
  const s = startOfWeek(d);
  s.setDate(s.getDate() + 6);
  return s;
}

function startOfMonth(y, m) {
  return new Date(y, m, 1);
}
function endOfMonth(y, m) {
  return new Date(y, m + 1, 0);
}

/** Return array of {n, start, end, label} for weeks that overlap with month(year,month).
 * A "week" is Mon–Sun. Week 1's Monday is the Monday of the ISO-week containing the 1st. */
export function weeksInMonth(year, month) {
  const first = startOfMonth(year, month);
  const last = endOfMonth(year, month);
  const w1Mon = startOfWeek(first);
  const weeks = [];
  for (let i = 0; i < 6; i++) {
    const s = new Date(w1Mon);
    s.setDate(w1Mon.getDate() + i * 7);
    const e = new Date(s);
    e.setDate(s.getDate() + 6);
    if (s > last) break;
    weeks.push({
      n: i + 1,
      start: s,
      end: e,
      label: `Week ${i + 1} · ${s.getDate()} ${s.toLocaleString("en-IN", { month: "short" })} – ${e.getDate()} ${e.toLocaleString("en-IN", { month: "short" })}`,
    });
  }
  return weeks;
}

export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function periodPresets(today = new Date()) {
  const yn = today.getFullYear();
  const mn = today.getMonth();
  const t = startOfDay(today);
  const y = new Date(t);
  y.setDate(t.getDate() - 1);

  const thisWkStart = startOfWeek(t);
  const thisWkEnd = endOfWeek(t);
  const lastWkStart = new Date(thisWkStart);
  lastWkStart.setDate(thisWkStart.getDate() - 7);
  const lastWkEnd = new Date(thisWkEnd);
  lastWkEnd.setDate(thisWkEnd.getDate() - 7);

  const thisMoStart = startOfMonth(yn, mn);
  const thisMoEnd = endOfMonth(yn, mn);
  const lastMoStart = startOfMonth(yn, mn - 1);
  const lastMoEnd = endOfMonth(yn, mn - 1);

  const q = Math.floor(mn / 3);
  const qStart = new Date(yn, q * 3, 1);
  const qEnd = endOfMonth(yn, q * 3 + 2);

  const yStart = new Date(yn, 0, 1);
  const yEnd = new Date(yn, 11, 31);

  // Financial year (India: Apr 1 → Mar 31)
  const fyStartYear = mn < 3 ? yn - 1 : yn;
  const fyStart = new Date(fyStartYear, 3, 1);
  const fyEnd = new Date(fyStartYear + 1, 2, 31);

  return {
    today: { key: "today", label: "Today", granularity: "day", start: t, end: t },
    yesterday: { key: "yesterday", label: "Yesterday", granularity: "day", start: y, end: y },
    this_week: { key: "this_week", label: "This Week", granularity: "week", start: thisWkStart, end: thisWkEnd },
    last_week: { key: "last_week", label: "Last Week", granularity: "week", start: lastWkStart, end: lastWkEnd },
    this_month: { key: "this_month", label: "This Month", granularity: "month", start: thisMoStart, end: thisMoEnd },
    last_month: { key: "last_month", label: "Last Month", granularity: "month", start: lastMoStart, end: lastMoEnd },
    this_quarter: { key: "this_quarter", label: "This Quarter", granularity: "quarter", start: qStart, end: qEnd },
    this_year: { key: "this_year", label: "This Year", granularity: "year", start: yStart, end: yEnd },
    financial_year: { key: "financial_year", label: `Financial Year (FY${String(fyStartYear).slice(2)}–${String(fyStartYear + 1).slice(2)})`, granularity: "year", start: fyStart, end: fyEnd },
  };
}

export function presetToPeriod(preset) {
  return {
    key: preset.key,
    label: preset.label,
    granularity: preset.granularity,
    start_date: iso(preset.start),
    end_date: iso(preset.end),
  };
}

export function weekPreset(week) {
  return {
    key: `w${week.n}`,
    label: week.label,
    granularity: "week",
    start_date: iso(week.start),
    end_date: iso(week.end),
  };
}

export function monthPreset(year, monthIdx) {
  const s = startOfMonth(year, monthIdx);
  const e = endOfMonth(year, monthIdx);
  return {
    key: `m${year}-${monthIdx}`,
    label: `${MONTH_NAMES[monthIdx]} ${year}`,
    granularity: "month",
    start_date: iso(s),
    end_date: iso(e),
  };
}

export function customPeriod(startISO, endISO) {
  return {
    key: "custom",
    label: `${startISO} → ${endISO}`,
    granularity: "custom",
    start_date: startISO,
    end_date: endISO,
  };
}

export function formatRange(startISO, endISO) {
  const s = new Date(startISO);
  const e = new Date(endISO);
  const same = s.getTime() === e.getTime();
  const fmt = (d) => d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  return same ? fmt(s) : `${fmt(s)} → ${fmt(e)}`;
}
